/**
 * Image Format Converter Utility
 * Automatically converts unsupported image formats to JPEG/PNG
 * for AI processing. NO COMPRESSION is applied to preserve quality.
 */

import heic2any from 'heic2any';

// Formats directly supported by the AI API
export const SUPPORTED_FORMATS = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

// Formats that can be converted to supported formats
export const CONVERTIBLE_FORMATS = [
    'image/heic',
    'image/heif',
    'image/bmp',
    'image/x-ms-bmp',
    'image/tiff',
    'image/tif',
    'image/gif',
    'image/avif',
    'image/svg+xml',
];

// File extensions for convertible formats
export const CONVERTIBLE_EXTENSIONS = [
    '.heic',
    '.heif',
    '.bmp',
    '.tiff',
    '.tif',
    '.gif',
    '.avif',
    '.svg',
];

export interface ConversionResult {
    file: File;
    wasConverted: boolean;
    originalFormat: string;
    convertedFormat: string;
}

/**
 * Check if a file's format needs conversion
 */
export function isConversionNeeded(file: File): boolean {
    const mimeType = file.type.toLowerCase();
    const extension = getFileExtension(file.name).toLowerCase();

    // Check if already supported
    if (SUPPORTED_FORMATS.includes(mimeType)) {
        return false;
    }

    // Check if can be converted by MIME type
    if (CONVERTIBLE_FORMATS.includes(mimeType)) {
        return true;
    }

    // Check by file extension (for HEIC which may not have correct MIME)
    if (CONVERTIBLE_EXTENSIONS.includes(extension)) {
        return true;
    }

    return false;
}

/**
 * Check if a file format is supported (either directly or after conversion)
 */
export function isFormatSupported(file: File): boolean {
    const mimeType = file.type.toLowerCase();
    const extension = getFileExtension(file.name).toLowerCase();

    return (
        SUPPORTED_FORMATS.includes(mimeType) ||
        CONVERTIBLE_FORMATS.includes(mimeType) ||
        CONVERTIBLE_EXTENSIONS.includes(extension)
    );
}

/**
 * Get file extension including the dot
 */
function getFileExtension(filename: string): string {
    const lastDot = filename.lastIndexOf('.');
    if (lastDot === -1) return '';
    return filename.substring(lastDot);
}

/**
 * Detect if a file is HEIC/HEIF format
 */
function isHeicFormat(file: File): boolean {
    const mimeType = file.type.toLowerCase();
    const extension = getFileExtension(file.name).toLowerCase();

    return (
        mimeType === 'image/heic' ||
        mimeType === 'image/heif' ||
        extension === '.heic' ||
        extension === '.heif'
    );
}

/**
 * Convert HEIC/HEIF image to JPEG using heic2any library
 * Uses quality 1.0 (no compression) to preserve original quality
 */
async function convertHeicToJpeg(file: File): Promise<File> {
    try {
        const blob = await heic2any({
            blob: file,
            toType: 'image/jpeg',
            quality: 1.0, // NO COMPRESSION - maximum quality
        });

        // heic2any may return an array of blobs for multi-image HEIC
        const resultBlob = Array.isArray(blob) ? blob[0] : blob;

        // Create new filename with .jpg extension
        const newFilename = file.name.replace(/\.(heic|heif)$/i, '.jpg');

        return new File([resultBlob], newFilename, { type: 'image/jpeg' });
    } catch (error) {
        console.error('HEIC conversion failed:', error);
        throw new Error(`Failed to convert HEIC image: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
}

/**
 * Convert image using Canvas API (for BMP, TIFF, GIF, SVG, AVIF)
 * Uses quality 1.0 (no compression) to preserve original quality
 * Outputs PNG for formats with transparency, JPEG otherwise
 */
async function convertWithCanvas(file: File): Promise<File> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onerror = () => reject(new Error('Failed to read file for conversion'));

        reader.onload = (e) => {
            const img = new Image();

            img.onerror = () => reject(new Error('Failed to load image for conversion'));

            img.onload = () => {
                try {
                    const canvas = document.createElement('canvas');
                    canvas.width = img.naturalWidth;
                    canvas.height = img.naturalHeight;

                    const ctx = canvas.getContext('2d');
                    if (!ctx) {
                        reject(new Error('Failed to get canvas context'));
                        return;
                    }

                    // Determine output format based on input
                    // Use PNG for formats that might have transparency (GIF, SVG, AVIF)
                    const mimeType = file.type.toLowerCase();
                    const hasTransparency =
                        mimeType === 'image/gif' ||
                        mimeType === 'image/svg+xml' ||
                        mimeType === 'image/avif' ||
                        mimeType === 'image/png';

                    const outputType = hasTransparency ? 'image/png' : 'image/jpeg';
                    const outputExt = hasTransparency ? '.png' : '.jpg';

                    // For JPEG output, fill with white background first
                    if (!hasTransparency) {
                        ctx.fillStyle = '#FFFFFF';
                        ctx.fillRect(0, 0, canvas.width, canvas.height);
                    }

                    // Draw the image at full quality
                    ctx.imageSmoothingEnabled = true;
                    ctx.imageSmoothingQuality = 'high';
                    ctx.drawImage(img, 0, 0);

                    // Convert to blob with maximum quality (1.0 = no compression)
                    canvas.toBlob(
                        (blob) => {
                            if (!blob) {
                                reject(new Error('Failed to create blob from canvas'));
                                return;
                            }

                            // Create new filename with appropriate extension
                            const baseName = file.name.replace(/\.[^.]+$/, '');
                            const newFilename = baseName + outputExt;

                            resolve(new File([blob], newFilename, { type: outputType }));
                        },
                        outputType,
                        1.0 // NO COMPRESSION - maximum quality
                    );
                } catch (error) {
                    reject(error);
                }
            };

            img.src = e.target?.result as string;
        };

        reader.readAsDataURL(file);
    });
}

/**
 * Convert an unsupported image format to a supported format
 * Uses quality 1.0 (no compression) to preserve original quality for AI analysis
 */
export async function convertToSupportedFormat(file: File): Promise<ConversionResult> {
    const originalFormat = file.type || 'unknown';

    // If already supported, return as-is
    if (!isConversionNeeded(file)) {
        return {
            file,
            wasConverted: false,
            originalFormat,
            convertedFormat: originalFormat,
        };
    }

    console.log(`Converting image from ${originalFormat} (${file.name})...`);

    let convertedFile: File;

    // Use appropriate conversion method
    if (isHeicFormat(file)) {
        convertedFile = await convertHeicToJpeg(file);
    } else {
        convertedFile = await convertWithCanvas(file);
    }

    console.log(`Conversion complete: ${file.name} -> ${convertedFile.name} (${convertedFile.type})`);

    return {
        file: convertedFile,
        wasConverted: true,
        originalFormat,
        convertedFormat: convertedFile.type,
    };
}

/**
 * Get a user-friendly format name for display
 */
export function getFormatDisplayName(mimeType: string): string {
    const formatNames: Record<string, string> = {
        'image/jpeg': 'JPEG',
        'image/jpg': 'JPEG',
        'image/png': 'PNG',
        'image/webp': 'WebP',
        'image/heic': 'HEIC (Apple)',
        'image/heif': 'HEIF (Apple)',
        'image/bmp': 'BMP',
        'image/x-ms-bmp': 'BMP',
        'image/tiff': 'TIFF',
        'image/tif': 'TIFF',
        'image/gif': 'GIF',
        'image/avif': 'AVIF',
        'image/svg+xml': 'SVG',
    };

    return formatNames[mimeType.toLowerCase()] || mimeType;
}

/**
 * Get the accept string for file inputs to allow all supported and convertible formats
 */
export function getAcceptString(): string {
    const allFormats = [
        ...SUPPORTED_FORMATS,
        ...CONVERTIBLE_FORMATS,
        ...CONVERTIBLE_EXTENSIONS,
    ];
    return allFormats.join(', ');
}
